import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { BankDetailsFindInterface } from '@zaoblako/shared/modules/client/dtos/bank.details/bank-details-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import { ContractFindInterface } from '@zaoblako/shared/modules/document/dtos/contract/contract-find.dto';
import type { DocumentCreateInterface } from '@zaoblako/shared/modules/document/dtos/document/document-create.dto';
import type { DocumentFindInterface } from '@zaoblako/shared/modules/document/dtos/document/document-find.dto';
import type { NdsFindInterface } from '@zaoblako/shared/modules/finance/dtos/nds-find.dto';
import type { TaskFindInterface } from '@zaoblako/shared/modules/task/dtos/task-find.dto';
import { BasisClientService } from '@zaoblako/web/core/services/basis/basis-client.service';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { ContragentService } from '@zaoblako/web/core/services/client/contragent.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { DealService } from '@zaoblako/web/core/services/deal/deal.service';
import { DocumentRegistryService } from '@zaoblako/web/core/services/document/document-registry.service';
import { DocumentTemplateService } from '@zaoblako/web/core/services/document/document-template.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { ContractService } from '@zaoblako/web/core/services/finance/contract.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { NdsService } from '@zaoblako/web/core/services/finance/nds.service';
import { TaskService } from '@zaoblako/web/core/services/task/task.service';
import { DocumentFormComponent } from '@zaoblako/web/modules/document/dumbs/document-form/document-form.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';

import * as moment from 'moment-timezone';
import { forkJoin, Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-document-form-sidebar',
  templateUrl: './document-form-sidebar.component.html',
  imports: [CommonModule, DocumentFormComponent, SpinnerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentFormSidebarComponent implements OnInit {
  @Input() public readonly index: number;

  public document = <DocumentFindInterface>{};
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public companies: CompanyFindInterface[] = [];
  public directions: DirectionFindInterface[] = [];
  public nds: NdsFindInterface[] = [];
  public bankDetails: BankDetailsFindInterface[] = [];

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly documentRegistryService: DocumentRegistryService,
    private readonly documentTemplateService: DocumentTemplateService,
    private readonly taskService: TaskService,
    private readonly companyService: CompanyService,
    private readonly contragentService: ContragentService,
    private readonly directionService: DirectionService,
    private readonly ndsService: NdsService,
    private readonly dealService: DealService,
    private readonly basisClientService: BasisClientService,
    private readonly contractService: ContractService,
    private readonly errorService: ErrorService,
    private readonly router: Router,
    private readonly confirmService: ConfirmDialogService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    const copyId = this.formStoreService.getState.forms[this.index]?.data?.copyId;
    const templateId = this.formStoreService.getState.forms[this.index]?.data?.templateId;
    const dealId = this.formStoreService.getState.forms[this.index]?.data?.dealId;
    const taskId = this.formStoreService.getState.forms[this.index]?.data?.taskId;
    const clientId = this.formStoreService.getState.forms[this.index]?.data?.clientId;
    const contractId = this.formStoreService.getState.forms[this.index]?.data?.contractId;

    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.document) {
      this.document.vatAmount = 0;
    }

    if (copyId) {
      this.documentRegistryService.findById<DocumentFindInterface>(copyId).subscribe((doc) => {
        const fieldsToCopy: Array<keyof DocumentFindInterface> = [
          'amount',
          'commentary',
          'company',
          'contragent',
          'deal',
          'direction',
          'externalCode',
          'code',
          'filial',
          'operationType',
          'documentType',
          'unit',
          'costCenter',
          'currency',
          'rate'
        ];
        (this.document as DocumentCreateInterface) = <DocumentCreateInterface>{};
        fieldsToCopy.forEach((key) => {
          (this.document as DocumentCreateInterface)[key] = doc[key];
        });
        if (isNaN(this.document.vatAmount)) {
          this.document.vatAmount = 0;
        }
        this.cdRef.markForCheck();
      });
    }

    if (templateId) {
      this.documentTemplateService.getDocumentFormByTemplate(templateId).subscribe((document) => {
        (this.document as DocumentCreateInterface) = document;
        if (isNaN(this.document.vatAmount)) {
          this.document.vatAmount = 0;
        }
        if (!this.document.date || isNaN(this.document.date.getTime())) {
          this.document.date = moment().toDate();
        }
      });
    }

    if (!isNaN(dealId)) {
      this.dealService.findById<DealFindInterface>(dealId).subscribe((deal) => {
        const dealFields: Array<keyof DealFindInterface> = [
          'amount',
          'company',
          'contragent',
          'filial',
          'direction',
          'unit'
        ];
        (this.document as DocumentCreateInterface) = <DocumentCreateInterface>{};
        dealFields.forEach((key) => {
          (this.document as DocumentCreateInterface)[key] = deal[key];
        });
        this.document.deal = deal;
        this.document.date = moment().toDate();
        this.cdRef.markForCheck();
      });
    }

    if (taskId) {
      this.taskService.findById<TaskFindInterface>(taskId).subscribe((task) => {
        const copiedFields: Array<keyof DocumentFindInterface> = [
          'amount',
          'contragent',
          'direction',
          'unit',
          'filial',
          'nds',
          'costCenter'
        ];
        (this.document as DocumentCreateInterface) = <DocumentCreateInterface>{};
        copiedFields.forEach((key) => {
          (this.document as DocumentCreateInterface)[key] = task[key];
        });
        this.document.company = task.organization;
        this.document.vatAmount = task.vat;
        (this.document as DocumentCreateInterface).isCreateLinkedDocument = true;
        this.cdRef.markForCheck();
      });
    }

    if (!isNaN(clientId)) {
      this.contragentService.search().subscribe((contragents) => {
        if (contragents.find((contragent) => contragent?.client?.id === clientId)) {
          this.document.contragent = contragents.find((contragent) => contragent.client?.id === clientId);
          this.cdRef.markForCheck();
        }
      });
    }

    if (!isNaN(contractId)) {
      this.contractService.findById<ContractFindInterface>(contractId).subscribe((contract) => {
        this.document.contract = contract;
        this.document.company = contract.company;
        this.document.contragent = contract.contragent;
        this.cdRef.markForCheck();
      });
    }

    forkJoin([
      this.companyService.find<CompanyFindInterface>(),
      this.directionService.find<DirectionFindInterface>(),
      this.ndsService.find<NdsFindInterface>()
    ]).subscribe(([companies, directions, nds]) => {
      this.companies = companies;
      this.directions = directions;
      this.nds = nds;
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  private getById() {
    this.formStoreService.setLoading(true, this.index);

    this.documentRegistryService
      .findById<DocumentFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (doc) => {
          this.document = doc;
          if (this.document?.bankDetails) {
            this.searchBankDetails(this.document?.bankDetails?.checkingAccount);
          }
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveDocument(document: DocumentCreateInterface) {
    if (this.formStoreService.getState.forms[this.index]?.entityId) {
      this.formStoreService.setLoading(true, this.index);

      this.documentRegistryService.updateByCode(this.document?.code, document).subscribe({
        next: (doc) => {
          this.formStoreService.setLoading(false, this.index);
          this.errorService.addSuccessMessage(`Документ "${this.document?.code}" обновлен`);
          this.formStoreService.saveNext(doc, this.index);
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    } else {
      this.formStoreService.setLoading(true, this.index);

      this.documentRegistryService.create<DocumentFindInterface>(this.document).subscribe({
        next: (doc) => {
          this.formStoreService.setLoading(false, this.index);
          this.errorService.addSuccessMessage('Документ создан!');

          const reportQueryParams = sessionStorage.getItem('report.documents.queryParams');
          if (reportQueryParams) {
            this.formStoreService.closeSidebar(this.index);
            this.router
              .navigate(['/document/list'], {
                queryParams: reportQueryParams ? JSON.parse(reportQueryParams) : {}
              })
              .catch(console.error);
            sessionStorage.removeItem('report.documents.queryParams');
          } else {
            this.formStoreService.saveNext(doc, this.index);
          }
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    }
  }

  public createBasedOn(): void {
    this.formStoreService.setLoading(true, this.index);

    this.documentRegistryService.createBasedOn(this.document.code).subscribe({
      next: (doc) => {
        this.formStoreService.setLoading(false, this.index);
        this.errorService.addSuccessMessage(`Создан документ "${doc.code}"!`);
        this.formStoreService.saveNext(doc, this.index);
      },
      error: () => {
        this.formStoreService.setLoading(false, this.index);
      }
    });
  }

  public deleteDocument() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить документ "${this.document?.code}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);

        this.documentRegistryService.deleteById(this.document?.code as any).subscribe(() => {
          this.formStoreService.setLoading(false, this.index);
          this.errorService.addSuccessMessage(`Документ "${this.document.code}" удален!`);
          this.formStoreService.deleteNext(this.index);
        });
      }
    });
  }

  public searchBankDetails(checkingAccount: string) {
    this.basisClientService.bankDetailsSearch({ search: checkingAccount }).subscribe((bankDetails) => {
      this.bankDetails = bankDetails;
      this.cdRef.markForCheck();
    });
  }
}
